import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  OrganizationWithMembership,
  OrganizationDetail,
  Member,
  Invitation,
} from '@/types/organization';
import type { Contact, VaultTagDefinition } from '@/types/vault';

interface OrganizationState {
  organizations: OrganizationWithMembership[];
  currentOrgId: string | null;
  currentOrg: OrganizationDetail | null;
  members: Member[];
  contacts: Contact[];
  /** True once the current org's full contact list has been loaded. */
  contactsLoaded: boolean;
  orgInvitations: Invitation[];
  myInvitations: Invitation[];
  tagDefinitions: VaultTagDefinition[];
  orgDataVersion: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: OrganizationState = {
  organizations: [],
  currentOrgId: null,
  currentOrg: null,
  members: [],
  contacts: [],
  contactsLoaded: false,
  orgInvitations: [],
  myInvitations: [],
  tagDefinitions: [],
  orgDataVersion: 0,
  isLoading: false,
  error: null,
};

// Read persisted org ID on init (read-only)
const loadPersistedCurrentOrg = (): string | null => {
  try {
    const stored = localStorage.getItem('ssp-enterprise-current-org');
    if (stored) {
      return stored;
    }
  } catch {
    // Ignore errors
  }
  return null;
};

const organizationSlice = createSlice({
  name: 'organization',
  initialState: {
    ...initialState,
    currentOrgId: loadPersistedCurrentOrg(),
  },
  reducers: {
    // Organizations
    setOrganizations: (state, action: PayloadAction<OrganizationWithMembership[]>) => {
      state.organizations = action.payload;
      state.isLoading = false;
      state.error = null;

      // If current org is not in the list, clear it and related data
      if (state.currentOrgId && !action.payload.find((o) => o.id === state.currentOrgId)) {
        state.currentOrgId = null;
        state.currentOrg = null;
        state.members = [];
        state.contacts = [];
        state.contactsLoaded = false;
        state.orgInvitations = [];
        state.tagDefinitions = [];
      }

      // Auto-select first org if none selected
      if (!state.currentOrgId && action.payload.length > 0) {
        const firstOrg = action.payload[0];
        if (firstOrg) {
          state.currentOrgId = firstOrg.id;
        }
      }
    },

    addOrganization: (state, action: PayloadAction<OrganizationWithMembership>) => {
      state.organizations.push(action.payload);
      // Auto-select if it's the first org
      if (!state.currentOrgId) {
        state.currentOrgId = action.payload.id;
      }
    },

    updateOrganization: (state, action: PayloadAction<OrganizationWithMembership>) => {
      const index = state.organizations.findIndex((o) => o.id === action.payload.id);
      if (index !== -1) {
        state.organizations[index] = action.payload;
      }
      if (state.currentOrg?.id === action.payload.id) {
        const { memberCount } = state.currentOrg;
        state.currentOrg = {
          ...action.payload,
          memberCount,
        };
      }
    },

    removeOrganization: (state, action: PayloadAction<string>) => {
      state.organizations = state.organizations.filter((o) => o.id !== action.payload);
      if (state.currentOrgId === action.payload) {
        state.currentOrgId = state.organizations[0]?.id || null;
        state.currentOrg = null;
        state.members = [];
        state.contacts = [];
        state.contactsLoaded = false;
        state.orgInvitations = [];
        state.tagDefinitions = [];
      }
    },

    // Current organization
    setCurrentOrgId: (state, action: PayloadAction<string | null>) => {
      state.currentOrgId = action.payload;
      state.currentOrg = null;
      state.members = [];
      state.contacts = [];
      state.contactsLoaded = false;
      state.orgInvitations = [];
      state.tagDefinitions = [];
    },

    setCurrentOrg: (state, action: PayloadAction<OrganizationDetail>) => {
      state.currentOrg = action.payload;
      state.currentOrgId = action.payload.id;
      state.isLoading = false;
    },

    clearCurrentOrg: (state) => {
      state.currentOrg = null;
      state.currentOrgId = null;
      state.members = [];
      state.contacts = [];
      state.contactsLoaded = false;
      state.orgInvitations = [];
      state.tagDefinitions = [];
    },

    // Members
    setMembers: (state, action: PayloadAction<Member[]>) => {
      state.members = action.payload;
    },

    updateMember: (state, action: PayloadAction<Member>) => {
      // Find by wkIdentity if available, otherwise by email or id
      const index = state.members.findIndex((m) => {
        if (action.payload.wkIdentity && m.wkIdentity) {
          return m.wkIdentity === action.payload.wkIdentity;
        }
        if (action.payload.email && m.email) {
          return m.email === action.payload.email;
        }
        return m.id === action.payload.id;
      });
      if (index !== -1) {
        state.members[index] = action.payload;
      }
    },

    // memberId can be wkIdentity, email, or id
    removeMember: (state, action: PayloadAction<string>) => {
      const memberId = action.payload;
      state.members = state.members.filter((m) => {
        // Check all possible identifiers
        if (m.wkIdentity === memberId) return false;
        if (m.email === memberId) return false;
        if (m.id === memberId) return false;
        return true;
      });
      if (state.currentOrg) {
        state.currentOrg.memberCount = state.members.length;
      }
    },

    addMember: (state, action: PayloadAction<Member>) => {
      state.members.push(action.payload);
      if (state.currentOrg) {
        state.currentOrg.memberCount = state.members.length;
      }
    },

    // Contacts
    setContacts: (state, action: PayloadAction<Contact[]>) => {
      state.contacts = action.payload;
      state.contactsLoaded = true;
    },

    addContact: (state, action: PayloadAction<Contact>) => {
      state.contacts.unshift(action.payload);
    },

    updateContactInState: (state, action: PayloadAction<Contact>) => {
      const index = state.contacts.findIndex((c) => c.id === action.payload.id);
      if (index !== -1) {
        state.contacts[index] = action.payload;
      }
    },

    removeContact: (state, action: PayloadAction<string>) => {
      state.contacts = state.contacts.filter((c) => c.id !== action.payload);
    },

    // Organization invitations
    setOrgInvitations: (state, action: PayloadAction<Invitation[]>) => {
      state.orgInvitations = action.payload;
    },

    addOrgInvitation: (state, action: PayloadAction<Invitation>) => {
      state.orgInvitations.unshift(action.payload);
    },

    updateOrgInvitation: (state, action: PayloadAction<Invitation>) => {
      const index = state.orgInvitations.findIndex((i) => i.id === action.payload.id);
      if (index !== -1) {
        state.orgInvitations[index] = action.payload;
      }
    },

    removeOrgInvitation: (state, action: PayloadAction<string>) => {
      state.orgInvitations = state.orgInvitations.filter((i) => i.id !== action.payload);
    },

    // My invitations
    setMyInvitations: (state, action: PayloadAction<Invitation[]>) => {
      state.myInvitations = action.payload;
    },

    removeMyInvitation: (state, action: PayloadAction<string>) => {
      state.myInvitations = state.myInvitations.filter((i) => i.id !== action.payload);
    },

    // Tag definitions (org-scoped)
    setTagDefinitions: (state, action: PayloadAction<VaultTagDefinition[]>) => {
      state.tagDefinitions = action.payload;
    },

    addTagDefinition: (state, action: PayloadAction<VaultTagDefinition>) => {
      state.tagDefinitions.push(action.payload);
    },

    updateTagDefinitionInState: (state, action: PayloadAction<VaultTagDefinition>) => {
      const index = state.tagDefinitions.findIndex((t) => t.id === action.payload.id);
      if (index !== -1) {
        state.tagDefinitions[index] = action.payload;
      }
    },

    removeTagDefinition: (state, action: PayloadAction<string>) => {
      state.tagDefinitions = state.tagDefinitions.filter((t) => t.id !== action.payload);
    },

    // Trigger org data re-fetch (members, invitations)
    incrementOrgDataVersion: (state) => {
      state.orgDataVersion += 1;
    },

    // Loading/Error states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Reset (on logout)
    reset: () => {
      return initialState;
    },
  },
});

export const {
  setOrganizations,
  addOrganization,
  updateOrganization,
  removeOrganization,
  setCurrentOrgId,
  setCurrentOrg,
  clearCurrentOrg,
  setMembers,
  updateMember,
  removeMember,
  addMember,
  setContacts,
  addContact,
  updateContactInState,
  removeContact,
  setOrgInvitations,
  addOrgInvitation,
  updateOrgInvitation,
  removeOrgInvitation,
  setMyInvitations,
  removeMyInvitation,
  setTagDefinitions,
  addTagDefinition,
  updateTagDefinitionInState,
  removeTagDefinition,
  incrementOrgDataVersion,
  setLoading,
  setError,
  reset,
} = organizationSlice.actions;

export default organizationSlice.reducer;
